/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "../services/reactQuery/reactQueryService";
import { isCookieFetched, mercureFetchCookie, mercureSubscribe } from "../services/mercure";
import { getProductData } from "../services/productService";
import { productClassNames } from "../types/Entities/AbstractCase";

export type AvailableAsyncFieldsMessage = {
  availableAsyncFields?: string[] | null;
};

function parseRequiredFields<TField extends string>(msg: AvailableAsyncFieldsMessage, requiredFields: readonly TField[]): TField[] {
  const raw = msg?.availableAsyncFields;
  if (!Array.isArray(raw)) return [];

  const allowed = new Set<string>(requiredFields as unknown as string[]);
  return raw.filter((v): v is TField => allowed.has(v));
}

type UseWaitForAsyncFieldsParams<TProduct extends { id: number; name: typeof productClassNames[number] }, TField extends keyof TProduct & string> = {
  product: TProduct;
  apiUri?: string;
  requiredFields: readonly TField[];
  timeoutMs?: number;
  topic?: string;
};

export function useWaitForAsyncFields<TProduct extends { id: number; name: typeof productClassNames[number] }, TField extends keyof TProduct & string>({
  product,
  apiUri,
  requiredFields,
  timeoutMs = 60_000,
  topic,
}: UseWaitForAsyncFieldsParams<TProduct, TField>) {
  const [cookieOk, setCookieOk] = useState<boolean>(() => isCookieFetched());
  const [availableAsyncFields, setAvailableAsyncFields] = useState<TField[]>([]);
  const [shouldWatchMercure, setShouldWatchMercure] = useState(true);
  const [shouldStartPolling, setShouldStartPolling] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const resolvedApiUri = useMemo(() => {
    return apiUri ?? getProductData(product.name, "apiUri");
  }, [apiUri, product.name]);

  const mercureTopic = useMemo(() => topic ?? `/case_data/async_data_available/${product.id}`, [topic, product.id]);

  const stopWatchingMercure = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    setShouldWatchMercure(false);
    setShouldStartPolling(true);
    stopWatchingMercure();
  }, [stopWatchingMercure]);

  const { data: refreshedProduct } = useQuery({
    queryKey: queryKeys.item(resolvedApiUri, product.id),
    queryFn: () => fetchResource(resolvedApiUri, product.id) as Promise<TProduct>,
    enabled: shouldStartPolling,
    refetchInterval: (query) => {
      const data = query.state.data;

      const stillWaiting = requiredFields.some((f) => !data || data[f] === null);
      return stillWaiting ? 2000 : false;
    },
    refetchIntervalInBackground: true,
  });

  const displayProduct = (refreshedProduct ?? product) as TProduct;
  const isWaiting = requiredFields.some((f) => displayProduct[f] === null);

  useEffect(() => {
    stopWatchingMercure();
    setAvailableAsyncFields([]);
    setShouldWatchMercure(true);
    setShouldStartPolling(false);
    setCookieOk(isCookieFetched());
  }, [product.id, stopWatchingMercure]);

  useEffect(() => {
    if (!isWaiting) return;
    if (!shouldWatchMercure) return;
    if (shouldStartPolling) return;
    if (cookieOk) return;

    mercureFetchCookie();

    const intervalId = window.setInterval(() => {
      if (isCookieFetched()) {
        setCookieOk(true);
        window.clearInterval(intervalId);
      }
    }, 250);

    return () => window.clearInterval(intervalId);
  }, [cookieOk, isWaiting, shouldStartPolling, shouldWatchMercure]);

  useEffect(() => {
    if (!isWaiting) return;
    if (!shouldWatchMercure) return;
    if (shouldStartPolling) return;
    if (timeoutRef.current !== null) return;

    timeoutRef.current = window.setTimeout(() => {
      startPolling();
    }, timeoutMs);
  }, [isWaiting, shouldStartPolling, shouldWatchMercure, startPolling, timeoutMs]);

  useEffect(() => {
    if (!isWaiting) return;
    if (!shouldWatchMercure) return;
    if (shouldStartPolling) return;
    if (!cookieOk) return;

    const eventSource = mercureSubscribe(mercureTopic, (msg: AvailableAsyncFieldsMessage) => {
      const incoming = parseRequiredFields(msg, requiredFields);
      if (incoming.length === 0) return;

      setAvailableAsyncFields((prev) => {
        const merged = new Set<TField>(prev);
        for (const f of incoming) merged.add(f);
        return Array.from(merged);
      });
    });

    eventSourceRef.current = eventSource;

    return () => {
      eventSource.close();
      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null;
      }
    };
  }, [cookieOk, isWaiting, mercureTopic, requiredFields, shouldStartPolling, shouldWatchMercure]);

  useEffect(() => {
    if (!isWaiting) return;
    if (!shouldWatchMercure) return;
    if (shouldStartPolling) return;

    const hasAll = requiredFields.every((f) => availableAsyncFields.includes(f));
    if (hasAll) startPolling();
  }, [availableAsyncFields, isWaiting, requiredFields, shouldStartPolling, shouldWatchMercure, startPolling]);

  useEffect(() => {
    if (!isWaiting) {
      setShouldWatchMercure(false);
      stopWatchingMercure();
    }
  }, [isWaiting, stopWatchingMercure]);

  return { data: displayProduct, isWaiting };
}
